import { getBackendUrl } from '../config/environment';
/**
 * Wrapper sécurisé pour les appels fetch avec retry, timeout et gestion d'erreurs
 * Résout les problèmes de "Failed to fetch" intermittents
 * ✅ Uses native fetch to avoid rrweb-recorder clone conflicts
 * 🔒 P2 FIX: Supports AbortController for graceful cancellation on navigation
 */

const API_BASE_URL = getBackendUrl();

// ✅ Use native fetch to prevent "Response body already used" errors from rrweb
const nativeFetch = typeof window !== 'undefined' 
  ? (window.__nativeFetch__ || window.fetch.bind(window))
  : fetch;

/**
 * Options par défaut pour safeFetch
 */
const DEFAULT_OPTIONS = {
  maxRetries: 3,
  retryDelay: 1000, // 1 seconde
  timeout: 10000, // 10 secondes
  backoffMultiplier: 2, // Délai x2 à chaque retry
};

/**
 * Crée un timeout pour les requêtes fetch
 */
function createTimeout(ms, signal) {
  return new Promise((_, reject) => {
    const timeoutId = setTimeout(() => {
      reject(new Error(`Request timeout after ${ms}ms`));
    }, ms);
    
    // 🔒 P2 FIX: Clear timeout if request is aborted
    if (signal) {
      signal.addEventListener('abort', () => {
        clearTimeout(timeoutId);
        reject(new DOMException('Request aborted', 'AbortError'));
      });
    }
  });
}

/**
 * Délai avec promesse
 */
function delay(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

/**
 * 🔒 P2 FIX: Check if error is an abort error (navigation interruption)
 */
function isAbortError(error) {
  return error.name === 'AbortError' || 
         error.message === 'Request aborted' ||
         error.message?.includes('aborted');
}

/**
 * Wrapper fetch sécurisé avec retry automatique, timeout et gestion d'erreurs
 * 
 * @param {string} url - URL relative ou absolue
 * @param {object} options - Options fetch standard (can include signal for AbortController)
 * @param {object} config - Configuration safeFetch (maxRetries, timeout, etc.)
 * @returns {Promise<Response>}
 */
export async function safeFetch(url, options = {}, config = {}) {
  const finalConfig = { ...DEFAULT_OPTIONS, ...config };
  const { maxRetries, retryDelay, timeout, backoffMultiplier } = finalConfig;
  
  // 🔒 P2 FIX: Extract abort signal from options
  const { signal, ...restOptions } = options;

  // Construire l'URL complète si elle est relative
  const fullUrl = url.startsWith('http') ? url : `${API_BASE_URL}${url}`;

  let lastError = null;
  let currentDelay = retryDelay;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    // 🔒 P2 FIX: Check if already aborted before attempting
    if (signal?.aborted) {
      console.log(`[safeFetch] ⏹️ Request aborted before attempt ${attempt + 1}`);
      throw new DOMException('Request aborted', 'AbortError');
    }
    
    try {
      console.log(`[safeFetch] Attempt ${attempt + 1}/${maxRetries + 1}: ${restOptions.method || 'GET'} ${fullUrl}`);

      // Race entre fetch et timeout - ✅ Use native fetch with abort signal
      const fetchPromise = nativeFetch(fullUrl, {
        ...restOptions,
        signal, // 🔒 P2 FIX: Pass abort signal to fetch
        headers: {
          'Content-Type': 'application/json',
          ...restOptions.headers,
        },
      });

      const response = await Promise.race([
        fetchPromise,
        createTimeout(timeout, signal),
      ]);

      // Vérifier le statut de la réponse
      if (!response.ok) {
        const errorText = await response.text().catch(() => 'Unknown error');
        console.error(`[safeFetch] HTTP ${response.status} - ${errorText}`);
        
        // Ne pas retry sur les erreurs 4xx (erreurs client)
        if (response.status >= 400 && response.status < 500) {
          throw new Error(`HTTP ${response.status}: ${errorText}`);
        }
        
        // Retry sur les erreurs 5xx (erreurs serveur)
        lastError = new Error(`HTTP ${response.status}: ${errorText}`);
        throw lastError;
      }

      console.log(`[safeFetch] ✅ Success: ${response.status}`);
      return response;

    } catch (error) {
      // 🔒 P2 FIX: Handle abort errors gracefully (no retry, no console error)
      if (isAbortError(error)) {
        console.log(`[safeFetch] ⏹️ Request aborted (navigation or unmount) - ${fullUrl}`);
        throw error; // Re-throw abort error without retry
      }
      
      lastError = error;
      console.error(`[safeFetch] ❌ Attempt ${attempt + 1} failed:`, error.message);

      // Si c'est la dernière tentative, on lance l'erreur
      if (attempt === maxRetries) {
        console.error(`[safeFetch] 💥 All ${maxRetries + 1} attempts failed for ${fullUrl}`);
        throw new Error(`Network request failed after ${maxRetries + 1} attempts: ${error.message}`);
      }

      // Attendre avant de retry (avec backoff exponentiel)
      console.log(`[safeFetch] ⏳ Retrying in ${currentDelay}ms...`);
      await delay(currentDelay);
      currentDelay *= backoffMultiplier;
    }
  }

  // Fallback (ne devrait jamais arriver)
  throw lastError || new Error('Unknown error in safeFetch');
}

/**
 * Helpers pour les méthodes HTTP courantes
 */
export const api = {
  /**
   * GET request
   */
  get: async (url, config = {}) => {
    const response = await safeFetch(url, { method: 'GET' }, config);
    return response.json();
  },

  /**
   * POST request
   */
  post: async (url, data, config = {}) => {
    const response = await safeFetch(
      url,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
      config
    );
    return response.json();
  },

  /**
   * PUT request
   */
  put: async (url, data, config = {}) => {
    const response = await safeFetch(
      url,
      {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(data),
      },
      config
    );
    return response.json();
  },

  /**
   * DELETE request
   */
  delete: async (url, config = {}) => {
    const response = await safeFetch(url, { method: 'DELETE' }, config);
    return response.json();
  },

  /**
   * POST avec token d'authentification
   */
  postAuth: async (url, data, token, config = {}) => {
    const response = await safeFetch(
      url,
      {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`,
        },
        body: JSON.stringify(data),
      },
      config
    );
    return response.json();
  },

  /**
   * GET avec token d'authentification
   */
  getAuth: async (url, token, config = {}) => {
    const response = await safeFetch(
      url,
      {
        method: 'GET',
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      },
      config
    );
    return response.json();
  },
};

export default api;
